import { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetOrganization,
  useUpdateOrganization,
  useUploadOrganizationLogo,
  useRemoveOrganizationLogo,
  useListModels,
  getGetOrganizationQueryKey,
} from '../../api/generated/generated';
import type { Organization, ModelConfig } from '../../api/generated';
import { SectionCard } from '../../components/common/SectionCard';
import { useSnackbar } from '../../components/common/SnackbarProvider';

export function OrgSettingsPage() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { data: org, isLoading } = useGetOrganization(orgId, {
    query: {
      select: (response): Organization | undefined =>
        response.status === 200 ? response.data : undefined,
    },
  });
  const { data: models } = useListModels({
    query: {
      select: (response): ModelConfig[] =>
        response.status === 200 ? response.data : [],
    },
  });
  const updateOrg = useUpdateOrganization({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetOrganizationQueryKey(orgId) });
      },
    },
  });
  const uploadLogo = useUploadOrganizationLogo({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetOrganizationQueryKey(orgId) });
      },
    },
  });
  const removeLogo = useRemoveOrganizationLogo({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetOrganizationQueryKey(orgId) });
      },
    },
  });
  const logoInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [description, setDescription] = useState('');
  const [valueProposition, setValueProposition] = useState('');
  const [messagingPoints, setMessagingPoints] = useState<string[]>([]);
  const [newPoint, setNewPoint] = useState('');

  useEffect(() => {
    if (org) {
      setName(org.name);
      setSelectedModel(org.selected_model ?? '');
      setDescription(org.description ?? '');
      setValueProposition(org.value_proposition ?? '');
      setMessagingPoints(org.messaging_points ?? []);
    }
  }, [org?.id]);

  const isOwner = user?.roles?.includes('org_owner') || user?.superAdmin;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isOwner) {
    return (
      <Box>
        <Alert severity="warning">Only organization owners can edit settings.</Alert>
      </Box>
    );
  }

  const handleAddPoint = () => {
    const trimmed = newPoint.trim();
    if (!trimmed || trimmed.length > 200 || messagingPoints.length >= 10) return;
    setMessagingPoints([...messagingPoints, trimmed]);
    setNewPoint('');
  };

  const handleRemovePoint = (index: number) => {
    setMessagingPoints(messagingPoints.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      const updates: {
        name?: string;
        selected_model?: string;
        description?: string;
        value_proposition?: string;
        messaging_points?: string[];
      } = {};
      if (name !== org?.name) updates.name = name;
      if (selectedModel !== org?.selected_model) updates.selected_model = selectedModel;
      if (description !== (org?.description ?? '')) updates.description = description;
      if (valueProposition !== (org?.value_proposition ?? ''))
        updates.value_proposition = valueProposition;
      if (JSON.stringify(messagingPoints) !== JSON.stringify(org?.messaging_points ?? []))
        updates.messaging_points = messagingPoints;

      if (Object.keys(updates).length === 0) {
        showSnackbar('No changes to save');
        return;
      }

      await updateOrg.mutateAsync({ orgId, data: updates });
      showSnackbar('Settings saved');
    } catch {
      showSnackbar('Failed to save settings', 'error');
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await uploadLogo.mutateAsync({ orgId, data: { file } });
      showSnackbar('Logo uploaded successfully.');
    } catch {
      showSnackbar('Failed to upload logo.', 'error');
    }

    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const handleLogoRemove = async () => {
    try {
      await removeLogo.mutateAsync({ orgId });
      showSnackbar('Logo removed.');
    } catch {
      showSnackbar('Failed to remove logo.', 'error');
    }
  };

  return (
    <Box>
      <SectionCard sx={{ maxWidth: 600, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{
          fontWeight: "bold"
        }}>
          Branding
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 2
          }}>
          Upload your company logo to white-label the platform. It replaces the Hanover logo
          in the sidebar. JPEG, PNG, or WebP, up to 2MB.
        </Typography>
        <Stack direction="row" spacing={2} sx={{
          alignItems: "center"
        }}>
          <Box
            sx={{
              width: 160,
              height: 64,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.default',
              overflow: 'hidden',
            }}
          >
            {org?.logo_url ? (
              <Box
                component="img"
                src={org.logo_url}
                alt={org.name}
                sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            ) : (
              <Typography variant="caption" sx={{
                color: "text.secondary"
              }}>
                No logo
              </Typography>
            )}
          </Box>
          <Box>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              ref={logoInputRef}
              onChange={handleLogoUpload}
            />
            <Button
              variant="outlined"
              startIcon={<PhotoCameraIcon />}
              onClick={() => logoInputRef.current?.click()}
              disabled={uploadLogo.isPending}
              size="small"
            >
              {uploadLogo.isPending ? 'Uploading...' : org?.logo_url ? 'Replace' : 'Upload'}
            </Button>
            {org?.logo_url && (
              <IconButton
                onClick={handleLogoRemove}
                disabled={removeLogo.isPending}
                size="small"
                sx={{ ml: 1 }}
                aria-label="Remove logo"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        </Stack>
      </SectionCard>
      <SectionCard sx={{ maxWidth: 600 }}>
        <TextField
          fullWidth
          label="Organization Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 3 }}
          slotProps={{
            htmlInput: { minLength: 3, maxLength: 100 }
          }}
        />

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" gutterBottom sx={{
          fontWeight: "bold"
        }}>
          Company Profile
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 2
          }}>
          This information helps the AI coach generate accurate, on-brand content
          including outreach emails and coaching feedback.
        </Typography>

        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          label="Company Description"
          placeholder="What does your organization do? What do you sell?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          helperText={`${description.length}/1000`}
          sx={{ mb: 3 }}
          slotProps={{
            htmlInput: { maxLength: 1000 }
          }}
        />

        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          label="Value Proposition"
          placeholder="How does your organization position itself? What makes it unique?"
          value={valueProposition}
          onChange={(e) => setValueProposition(e.target.value)}
          helperText={`${valueProposition.length}/1000`}
          sx={{ mb: 3 }}
          slotProps={{
            htmlInput: { maxLength: 1000 }
          }}
        />

        <Typography variant="body2" sx={{ mb: 1 }}>
          Key Messaging Points
        </Typography>
        {messagingPoints.length < 10 && (
          <TextField
            fullWidth
            size="small"
            placeholder="Add a messaging point and press Enter"
            value={newPoint}
            onChange={(e) => setNewPoint(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddPoint();
              }
            }}
            helperText={`${messagingPoints.length}/10 points`}
            sx={{ mb: 1 }}
            slotProps={{
              htmlInput: { maxLength: 200 }
            }}
          />
        )}
        {messagingPoints.length >= 10 && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              mb: 1,
              display: 'block'
            }}>
            Maximum 10 messaging points reached.
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          {messagingPoints.map((point, i) => (
            <Chip
              key={i}
              label={point}
              onDelete={() => handleRemovePoint(i)}
              size="small"
            />
          ))}
        </Box>

        <Divider sx={{ my: 3 }} />

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>AI Model</InputLabel>
          <Select
            value={selectedModel}
            label="AI Model"
            onChange={(e) => setSelectedModel(e.target.value)}
          >
            {models?.map((model) => (
              <MenuItem key={model.id} value={model.id}>
                {model.name} {model.default ? '(Default)' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={updateOrg.isPending}
        >
          {updateOrg.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </SectionCard>
    </Box>
  );
}
